import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertCircle, FileText, Trash2, Upload } from "lucide-react";
import api, { API } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useConfirm } from "@/contexts/ConfirmContext";

const ACCEPT = ".pdf,.docx,.txt,.md,.markdown,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown";
const MAX_BYTES = 15 * 1024 * 1024;

/**
 * Writer knowledge base — document uploads that are inlined into The Brand
 * Writer system prompt on every generation. Lives directly under the Writer
 * textarea in AI Instructions.
 */
export default function WriterKnowledge({ brandId, disabled }) {
  const { t } = useI18n();
  const confirm = useConfirm();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/brands/${brandId}/writer-knowledge`);
      setDocs(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [brandId]);

  useEffect(() => { refresh(); }, [refresh]);

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(t("settings.ai_writer_kb_upload_failed") + ": " + (file.name) + " > 15 MB");
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem("bh_token");
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/brands/${brandId}/writer-knowledge`, {
        method: "POST", body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!r.ok) {
        const detail = await r.json().catch(() => ({ detail: "" }));
        throw new Error(detail?.detail || `HTTP ${r.status}`);
      }
      await refresh();
    } catch (err) {
      toast.error(err.message || t("settings.ai_writer_kb_upload_failed"));
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    for (const f of files) await uploadFile(f);
  };

  const removeDoc = async (doc) => {
    const ok = await confirm({
      title: "Document verwijderen?",
      body: t("settings.ai_writer_kb_remove_confirm").replace("{name}", doc.filename),
      confirmLabel: "Verwijderen",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/brands/${brandId}/writer-knowledge/${doc.id}`);
      setDocs((prev) => prev.filter((d) => d.id !== doc.id));
      toast.success(t("settings.ai_writer_kb_removed"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("settings.ai_writer_kb_removed_failed"));
    }
  };

  // Drag-drop handlers — stop propagation so dropping over the dropzone
  // doesn't trigger the browser's default "open file" navigation.
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    handleFiles(e.dataTransfer?.files);
  };

  return (
    <div className="mt-4" data-testid="writer-knowledge">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-900">{t("settings.ai_writer_kb_title")}</div>
          <div className="text-xs text-slate-500">{t("settings.ai_writer_kb_sub")}</div>
        </div>
      </div>

      {/* Dropzone */}
      <button
        type="button"
        data-testid="writer-knowledge-dropzone"
        disabled={disabled || uploading}
        onClick={() => inputRef.current?.click()}
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`w-full rounded-lg border-2 border-dashed px-4 py-6 flex flex-col items-center justify-center gap-2 transition-colors text-center ${
          disabled
            ? "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed"
            : dragOver
            ? "border-slate-900 bg-slate-50"
            : "border-slate-200 bg-white hover:border-slate-400 hover:bg-slate-50"
        }`}
      >
        <div className="w-9 h-9 rounded-md bg-slate-100 flex items-center justify-center">
          <Upload className="w-4 h-4 text-slate-700" strokeWidth={1.75} />
        </div>
        <div className="text-sm text-slate-700">
          {uploading ? t("settings.ai_writer_kb_uploading") : t("settings.ai_writer_kb_drop")}
        </div>
        <div className="text-[11px] text-slate-400">{t("settings.ai_writer_kb_formats")}</div>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = ""; // allow re-picking the same file
        }}
        data-testid="writer-knowledge-file-input"
      />

      {/* Documents list */}
      {loading ? null : docs.length === 0 ? (
        <div
          className="mt-3 rounded-md border border-dashed border-slate-200 bg-white py-6 text-center text-sm text-slate-500"
          data-testid="writer-knowledge-empty"
        >
          {t("settings.ai_writer_kb_empty")}
        </div>
      ) : (
        <ul className="mt-3 divide-y divide-slate-100 bg-white border border-slate-100 rounded-md" data-testid="writer-knowledge-list">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`writer-knowledge-item-${d.id}`}>
              <div className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                <FileText className="w-4 h-4 text-slate-700" strokeWidth={1.75} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-900 truncate" title={d.filename}>{d.filename}</div>
                <div className="text-[11px] text-slate-500 flex items-center gap-1.5 flex-wrap">
                  <span>{humanSize(d.size)}</span>
                  {!d.has_text && (
                    <>
                      <span className="text-slate-300">·</span>
                      <span className="inline-flex items-center gap-1 text-amber-700">
                        <AlertCircle className="w-3 h-3" strokeWidth={1.75} />
                        {t("settings.ai_writer_kb_no_text")}
                      </span>
                    </>
                  )}
                  {d.truncated && (
                    <>
                      <span className="text-slate-300">·</span>
                      <span className="text-amber-700">{t("settings.ai_writer_kb_truncated")}</span>
                    </>
                  )}
                </div>
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeDoc(d)}
                  aria-label={t("settings.ai_writer_kb_remove")}
                  data-testid={`writer-knowledge-remove-${d.id}`}
                  className="w-8 h-8 rounded-md flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function humanSize(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
